import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { environment } from '../config/environment';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, signIn, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [gamePin, setGamePin] = useState('');
  const [pinError, setPinError] = useState('');

  const displayName = user?.name || user?.given_name || user?.email || "User";
  const initial = (displayName[0] || "?").toUpperCase();
  const isProUser = user && user['custom:plan'] === 'pro';

  const handleJoinGame = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPin = gamePin.trim().toUpperCase();

    if (!trimmedPin) {
      setPinError('Please enter a game PIN');
      return;
    }

    if (trimmedPin.length < 3) {
      setPinError('Game PIN is too short');
      return;
    }

    // Redirect to player webapp with game PIN
    window.location.href = `${environment.playerUrl}/${trimmedPin}`;
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <nav className="mx-auto max-w-7xl px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
        <div className="flex items-baseline gap-3 sm:gap-6 lg:gap-8">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="text-xl sm:text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors"
            >
              quizriot.live
            </button>
            {isAuthenticated && (
              <span className={`px-2 py-0.5 text-xs font-bold rounded-full inline-flex items-center ${
                isProUser
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {isProUser ? 'PRO' : 'FREE'}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/about')}
            className="hidden sm:block text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            About
          </button>
        </div>

        <div className="flex items-center gap-3">
          {!isAuthenticated && (
            <>
              {/* Join Game Input - Hidden on mobile, visible on tablet+ */}
              <form onSubmit={handleJoinGame} className="hidden md:flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    value={gamePin}
                    onChange={(e) => {
                      setGamePin(e.target.value.toUpperCase());
                      setPinError('');
                    }}
                    placeholder="Game PIN"
                    className="w-32 px-3 py-2 text-sm font-semibold uppercase text-center rounded-lg border-2 border-blue-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                    maxLength={20}
                  />
                  {pinError && (
                    <div className="absolute top-full mt-1 left-0 right-0 text-xs text-red-600 text-center whitespace-nowrap">
                      {pinError}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
                  title="Join Game"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                  <span className="hidden lg:inline">Join</span>
                </button>
              </form>
              <button
                onClick={signIn}
                disabled
                title="Sign-in is temporarily disabled while we re-architect QuizCrunch"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-300 text-gray-500 text-sm font-semibold rounded-lg cursor-not-allowed min-h-[44px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <path d="M10 17l5-5-5-5"/><path d="M15 12H3"/>
                </svg>
                <span>Sign in</span>
              </button>
            </>
          )}

          {isAuthenticated && (
            <div className="relative flex items-center gap-3" ref={menuRef}>
              {/* User Info and Avatar */}
              <div className="hidden lg:flex flex-col items-end">
                <span className="text-xs font-medium text-gray-700 max-w-[150px] truncate">
                  {displayName}
                </span>
                <span className="text-xs text-gray-500 max-w-[150px] truncate">
                  {user?.email}
                </span>
              </div>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="size-10 min-h-[44px] min-w-[44px] rounded-full bg-orange-500 text-white grid place-items-center text-sm font-bold hover:bg-orange-600 transition-all shadow-md hover:shadow-lg"
                title={`${displayName} - Menu`}
              >
                {initial}
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white border border-gray-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>

                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    View Profile
                  </button>

                  {isProUser && (
                    <form action={`${environment.api.stripe}/create-portal-session`} method="POST">
                      <input type="hidden" name="email" value={user?.email || ''} />
                      <button
                        type="submit"
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors flex items-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 6v6l4 2"/>
                        </svg>
                        Manage Subscription
                      </button>
                    </form>
                  )}

                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        signOut();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                        <path d="M16 17l5-5-5-5"/>
                        <path d="M21 12H9"/>
                      </svg>
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;